import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { HttpEvent } from '@angular/common/http';
import { combineLatest, distinctUntilChanged, Observable, of, shareReplay } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { ImageApi } from '@domains/image/data-access/image.api';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ImageCategory } from '@domains/image/model/api/image-category.interface';
import { ImageDataNew } from '@domains/image/model/api/image-data-new.interface';
import { ImageDataUpdateRequest } from '@domains/image/model/api/image-data-update-request.interface';
import { PagePagination } from '@shared/model/page-pagination.interface';
import { DeleteImagesResponse } from '@domains/image/model/api/image-delete-response.interface';
import { GalleryContentType } from '@domains/image/model/gallery-content-type.enum';

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #imageApi = inject(ImageApi);

  readonly #categories = signal<ImageCategory[]>([]);

  readonly categories = this.#categories.asReadonly();

  readonly #imagesById = signal<Map<string, ImageDataNew>>(new Map<string, ImageDataNew>());

  readonly #imagesById$ = toObservable(this.#imagesById);

  readonly #globalImagesLoaded = signal(false);

  readonly #globalImagesLoaded$ = toObservable(this.#globalImagesLoaded);

  readonly #urlCache = new Map<string, Observable<string>>();

  readonly imagesById = this.#imagesById.asReadonly();

  constructor() {
    this.loadGlobalImagesState();
    this.getCategoryList();
  }

  getCategoryList(): void {
    this.#imageApi
      .categoryList()
      .pipe(take(1), takeUntilDestroyed())
      .subscribe({
        next: pageCategories => {
          this.#categories.set(pageCategories.items);
        },
      });
  }

  isLocalPath(id: string): boolean {
    return id.startsWith('assets/');
  }

  isAbsoluteUrl(id: string): boolean {
    return /^https?:\/\//i.test(id) || id.startsWith('data:') || id.startsWith('blob:');
  }

  isS3Key(id: string): boolean {
    return id.startsWith('Images');
  }

  getUrlById(id: string | null | undefined, placeholder?: string): string | null {
    if (!id) {
      return placeholder;
    }

    if (this.isAbsoluteUrl(id) || this.isLocalPath(id)) {
      return id;
    }

    if (this.isS3Key(id)) {
      const hit = this.#imagesById().get(id)?.imageUrl;

      return hit ?? placeholder;
    }

    return id;
  }

  getUrlById$(id: string | null | undefined, placeholder = ''): Observable<string> {
    if (!id) {
      return of(placeholder);
    }

    if (this.isAbsoluteUrl(id) || this.isLocalPath(id)) {
      return of(id);
    }

    if (this.isS3Key(id)) {
      const cacheKey = `${id}:${placeholder}`;
      const cached = this.#urlCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const stream$ = combineLatest([this.#imagesById$, this.#globalImagesLoaded$]).pipe(
        map(([images, globalImagesLoaded]) => {
          const imageUrl = images.get(id)?.imageUrl;

          if (imageUrl) {
            return imageUrl;
          }

          return globalImagesLoaded ? placeholder : null;
        }),
        distinctUntilChanged(),
        filter((url): url is string => url !== null),
        take(1),
        shareReplay(1),
      );

      this.#urlCache.set(cacheKey, stream$);

      return stream$;
    }

    return of(id);
  }

  #setImages(images: ImageDataNew[]): void {
    const imagesById = new Map<string, ImageDataNew>();

    images.forEach(image => {
      imagesById.set(image.imageKey, image);
    });

    this.#imagesById.set(imagesById);
  }

  loadGlobalImagesState(): void {
    this.#imageApi
      .imageList(1, 9999)
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: page => {
          this.#setImages(page.items);
          this.#globalImagesLoaded.set(true);
        },
        error: () => {
          this.#globalImagesLoaded.set(true);
        },
      });
  }

  imageList(
    pageNumber: number,
    itemsPerPage: number,
    categoryIds: number[],
    searchValue: string,
    contentType: GalleryContentType,
  ): Observable<PagePagination<ImageDataNew>> {
    return this.#imageApi.imageList(pageNumber, itemsPerPage, categoryIds, searchValue, contentType);
  }

  uploadWithProgress(file: File, name: string): Observable<HttpEvent<ImageDataNew[]>> {
    return this.#imageApi.uploadWithProgress(file, name);
  }

  updateImagesInfoBatch(images: ImageDataUpdateRequest[]): Observable<ImageDataNew[]> {
    return this.#imageApi.updateImagesInfoBatch(images);
  }

  createCategory(categoryList: ImageCategory[]): Observable<ImageCategory[]> {
    return this.#imageApi.createCategory(categoryList);
  }

  deleteImagesByIds(ids: number[]): Observable<DeleteImagesResponse> {
    return this.#imageApi.deleteImagesByIds(ids);
  }

  addNewCategory(category: ImageCategory): void {
    this.#categories.update(categories => {
      return [category, ...categories];
    });
  }
}
